import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { RefreshCw, CheckCircle2, XCircle, Settings2, ListOrdered, BellRing, Coins } from "lucide-react";
import { TwilioConfigTab } from "@/components/admin/twilio/TwilioConfigTab";
import { TwilioJournalTab } from "@/components/admin/twilio/TwilioJournalTab";
import { TwilioEventsTab } from "@/components/admin/twilio/TwilioEventsTab";

const TABS = [
  { key: "config", label: "Configuration", icon: Settings2 },
  { key: "journal", label: "Journal", icon: ListOrdered },
  { key: "events", label: "Événements SMS", icon: BellRing },
];

export default function AdminTwilio() {
  const [cfg, setCfg] = useState(null);
  const [stats, setStats] = useState(null);
  const [cost, setCost] = useState(null);
  const [tab, setTab] = useState("config");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: s }, { data: st }, { data: co }] = await Promise.all([api.get("/admin/twilio/settings"), api.get("/admin/twilio/stats"), api.get("/admin/twilio/cost")]);
      setCfg(s);
      setStats(st);
      setCost(co);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-full" data-testid="admin-twilio">
      <PageHeader
        eyebrow="Admin · Communication"
        title="SMS (Twilio)"
        sub="Identifiants chiffrés en base, accusés de réception, renvoi automatique des SMS en attente et interrupteurs par événement."
        right={
          <Button variant="outline" onClick={load} disabled={loading} data-testid="tw-refresh" className="h-11">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />Recharger
          </Button>
        }
      />

      <div className="p-6 lg:p-10 space-y-6 max-w-6xl">
        <div className={`p-4 border-2 flex items-center gap-3 ${cfg?.enabled ? "bg-emerald-50 border-emerald-500" : "bg-orange-50 border-orange-400"}`} data-testid="tw-status">
          {cfg?.enabled ? <CheckCircle2 className="h-6 w-6 text-[#059669]" /> : <XCircle className="h-6 w-6 text-[#F59E0B]" />}
          <div className="flex-1">
            <div className="font-display font-bold" data-testid="tw-status-title">{cfg?.enabled ? "Twilio opérationnel" : "Twilio non configuré — mode « en attente »"}</div>
            <div className="text-xs text-[#495057]">{cfg?.enabled
              ? `SID : ${cfg.account_sid?.slice(0, 10)}… · Expéditeur : ${cfg.sender}${cfg.sender_kind === "messaging_service" ? " (Messaging Service)" : ""}${cfg.verified_at ? " · identifiants vérifiés chez Twilio" : ""}`
              : "Les SMS sont journalisés « en attente » et partiront automatiquement dès que vous aurez enregistré un Account SID, un Auth Token et un numéro (ou un Messaging Service)."}</div>
          </div>
          {cfg?.pending_retry > 0 && (
            <span className="text-[10px] uppercase tracking-wider font-bold bg-[#F59E0B] text-white px-2 py-1" data-testid="tw-pending-count">
              {cfg.pending_retry} en attente
            </span>
          )}
        </div>

        {cost?.over_threshold && (
          <div className="p-3 border-2 border-[#E60000] bg-[#FEF2F2] flex items-center gap-3 text-sm" data-testid="tw-cost-banner">
            <Coins className="h-5 w-5 text-[#E60000]" />
            <div className="flex-1"><b>Budget SMS du mois dépassé :</b> {cost.current.estimated_cost.toFixed(2).replace(".", ",")} € estimés ({cost.current.sms_count} SMS, {cost.current.segments} segments) pour un seuil de {cost.settings.monthly_threshold} €. Coupez des événements SMS ou ajustez le seuil dans l'onglet Configuration.</div>
          </div>
        )}

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-7 gap-3" data-testid="tw-stats">
            {[
              ["Total", stats.total, "#002FA7"],
              ["Envoyés", stats.sent, "#059669"],
              ["Livrés", stats.delivered, "#059669"],
              ["En attente", stats.not_sent_no_provider, "#D97706"],
              ["Non livrés", stats.undelivered, "#E60000"],
              ["Erreurs", stats.error, "#E60000"],
              ["Coût du mois", cost ? `${cost.current.estimated_cost.toFixed(2).replace(".", ",")} €` : "—", cost?.over_threshold ? "#E60000" : "#F59E0B"],
            ].map(([label, val, color]) => (
              <div key={label} className="bg-white border border-[#DEE2E6] p-4" data-testid={`tw-stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color }}>{label}</div>
                <div className="font-display font-black text-3xl mt-1">{val ?? 0}</div>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-wrap gap-1.5" role="tablist">
          {TABS.map((t) => (
            <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} onClick={() => setTab(t.key)} data-testid={`tw-tab-${t.key}`}
                    className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold px-3 py-2 border transition ${
                      tab === t.key ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-[#DEE2E6] hover:border-[#0A0A0A]"}`}>
              <t.icon className="h-3.5 w-3.5" />{t.label}
            </button>
          ))}
        </div>

        {tab === "config" && <TwilioConfigTab cfg={cfg} onChanged={load} onCostLoaded={setCost} />}
        {tab === "journal" && <TwilioJournalTab cfg={cfg} onChanged={load} />}
        {tab === "events" && <TwilioEventsTab />}
      </div>
    </div>
  );
}
